import React, { useState } from "react";
import { useQuery } from "react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { BsPeople, BsBullseye, BsGraphUpArrow, BsFacebook, BsGlobe2, BsExclamationTriangle } from "react-icons/bs";
import analyticsDashboardApi from "../../../../services/analyticsDashboard.service";
import { formatNumber, formatPercentage } from "../../../../utils/analyticsDashboardFormat";
import { axisDateTick } from "../../../../utils/analyticsChartLabels";
import { renderPiePercentLabel } from "./PiePercentLabel.component";
import {
  WidgetSection,
  MetricsGrid,
  MetricCard,
  MetricCardHeader,
  MetricLabel,
  MetricIconWrap,
  MetricValue,
  ChartsGrid,
  ChartCard,
  ChartCardTitle,
  SkeletonBlock,
  WidgetTable,
  TableScroll,
  SubTabsBar,
  SubTabButton,
  StatusPill,
  EmptyStateBox,
  MetricTooltip,
} from "./Widgets.styles";

const COLORS = ["#6366f1", "#3b82f6", "#10b981"];

const tooltipStyle = {
  contentStyle: {
    background: "white",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    boxShadow: "0 6px 18px rgba(15,23,42,0.10)",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.82rem",
  },
  labelStyle: { fontWeight: 700, color: "#111827", marginBottom: 4 },
  itemStyle: { color: "#4B5563" },
};

const ClientSourceAnalysisWidget = ({ query }) => {
  const [subTab, setSubTab] = useState("trends");

  const { data, isLoading, error } = useQuery(
    ["client-source-analysis", query.companyId, query.startDate, query.endDate, query.channelIds, query.granularity],
    () => analyticsDashboardApi.getClientSourceAnalysis(query),
    { enabled: !!query.companyId }
  );

  if (isLoading) {
    return (
      <MetricsGrid columns={4}>
        {[...Array(4)].map((_, i) => (
          <MetricCard key={i}><SkeletonBlock style={{ height: 48 }} /></MetricCard>
        ))}
      </MetricsGrid>
    );
  }

  if (error) {
    return (
      <EmptyStateBox>
        <BsExclamationTriangle size={32} style={{ color: "#DC2626", marginBottom: 12 }} />
        <div>Error al cargar análisis de fuentes de conversaciones</div>
      </EmptyStateBox>
    );
  }

  if (!data?.data) {
    return <EmptyStateBox>No hay datos de análisis de fuentes de conversaciones disponibles.</EmptyStateBox>;
  }

  const { summary = {}, timeSeries = [] } = data.data;
  const emptySource = { clients: 0, finished: 0, conversionRate: 0 };
  const sourceBreakdown = {
    fbForm: summary.sourceBreakdown?.fbForm || emptySource,
    fbAds: summary.sourceBreakdown?.fbAds || emptySource,
    organic: summary.sourceBreakdown?.organic || emptySource,
  };
  const pieData = [
    { name: "Formularios de Facebook", value: sourceBreakdown.fbForm.clients },
    { name: "Anuncios de Facebook", value: sourceBreakdown.fbAds.clients },
    { name: "Orgánico", value: sourceBreakdown.organic.clients },
  ];

  return (
    <WidgetSection>
      <MetricsGrid columns={4}>
        <MetricCard accentColor="#6366f1">
          <MetricCardHeader>
            <MetricLabel>
              Total de Conversaciones
              <MetricTooltip text="Total de conversaciones únicas registradas en el período, provenientes de todas las fuentes (formularios FB, anuncios FB y tráfico orgánico)." />
            </MetricLabel>
            <MetricIconWrap bg="#EEF2FF" color="#6366f1"><BsPeople /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(summary.totalClients)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#10b981">
          <MetricCardHeader>
            <MetricLabel>
              Total Finalizadas
              <MetricTooltip text="Conversaciones cerradas exitosamente durante el período, sumando todas las fuentes de origen." />
            </MetricLabel>
            <MetricIconWrap bg="#F0FDF4" color="#10b981"><BsBullseye /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(summary.totalFinished)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#8b5cf6">
          <MetricCardHeader>
            <MetricLabel>
              Tasa General
              <MetricTooltip text="Tasa de conversión global: (Total Finalizadas / Total Conversaciones) × 100, considerando todas las fuentes." />
            </MetricLabel>
            <MetricIconWrap bg="#F5F3FF" color="#8b5cf6"><BsGraphUpArrow /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatPercentage(summary.overallConversionRate)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#3b82f6">
          <MetricCardHeader>
            <MetricLabel>
              Tasa Formularios FB
              <MetricTooltip text="Tasa de conversión específica de conversaciones originadas por formularios de Facebook Lead Ads." />
            </MetricLabel>
            <MetricIconWrap bg="#EFF6FF" color="#3b82f6"><BsFacebook /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatPercentage(sourceBreakdown.fbForm.conversionRate)}</MetricValue>
        </MetricCard>
      </MetricsGrid>

      <ChartsGrid columns={3}>
        <MetricCard accentColor="#6366f1">
          <MetricLabel>Formularios de Facebook</MetricLabel>
          <MetricValue>{formatNumber(sourceBreakdown.fbForm.clients)}</MetricValue>
          <p style={{ fontSize: "0.8rem", color: "#6B7280", margin: "4px 0 0" }}>
            {formatNumber(sourceBreakdown.fbForm.finished)} finalizados ({formatPercentage(sourceBreakdown.fbForm.conversionRate)})
          </p>
        </MetricCard>
        <MetricCard accentColor="#3b82f6">
          <MetricLabel>Anuncios de Facebook</MetricLabel>
          <MetricValue>{formatNumber(sourceBreakdown.fbAds.clients)}</MetricValue>
          <p style={{ fontSize: "0.8rem", color: "#6B7280", margin: "4px 0 0" }}>
            {formatNumber(sourceBreakdown.fbAds.finished)} finalizados ({formatPercentage(sourceBreakdown.fbAds.conversionRate)})
          </p>
        </MetricCard>
        <MetricCard accentColor="#10b981">
          <MetricLabel>Orgánico</MetricLabel>
          <MetricValue>{formatNumber(sourceBreakdown.organic.clients)}</MetricValue>
          <p style={{ fontSize: "0.8rem", color: "#6B7280", margin: "4px 0 0" }}>
            {formatNumber(sourceBreakdown.organic.finished)} finalizados ({formatPercentage(sourceBreakdown.organic.conversionRate)})
          </p>
        </MetricCard>
      </ChartsGrid>

      <SubTabsBar>
        <SubTabButton active={subTab === "trends"} onClick={() => setSubTab("trends")}>Tendencias</SubTabButton>
        <SubTabButton active={subTab === "distribution"} onClick={() => setSubTab("distribution")}>Distribución</SubTabButton>
        <SubTabButton active={subTab === "data"} onClick={() => setSubTab("data")}>Tabla de Datos</SubTabButton>
      </SubTabsBar>

      {subTab === "trends" && (
        <ChartCard>
          <ChartCardTitle>
            Tendencias de Fuentes de Conversaciones
            <MetricTooltip text="Evolución diaria de conversaciones por fuente de origen (formularios FB, anuncios FB y orgánico). Las áreas apiladas muestran la composición del volumen total." />
          </ChartCardTitle>
          <ResponsiveContainer width="100%" height={420}>
            <AreaChart data={timeSeries} margin={{ top: 8, right: 24, left: 0, bottom: 16 }}>
              <defs>
                <linearGradient id="gradFbForm" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS[0]} stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="gradFbAds" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS[1]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS[1]} stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="gradOrganic" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS[2]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS[2]} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="date" tickFormatter={axisDateTick} minTickGap={24} interval="preserveStartEnd" height={32} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
              <Area type="monotone" dataKey="fbFormClients" stackId="1" stroke={COLORS[0]} fill="url(#gradFbForm)" strokeWidth={2} name="Formularios FB" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
              <Area type="monotone" dataKey="fbAdsClients" stackId="1" stroke={COLORS[1]} fill="url(#gradFbAds)" strokeWidth={2} name="Anuncios FB" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
              <Area type="monotone" dataKey="organicClients" stackId="1" stroke={COLORS[2]} fill="url(#gradOrganic)" strokeWidth={2} name="Orgánico" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {subTab === "distribution" && (
        <ChartsGrid columns={2}>
          <ChartCard>
            <ChartCardTitle>
              Distribución de Conversaciones por Fuente
              <MetricTooltip text="Proporción de conversaciones por cada fuente de origen en el período. Útil para identificar qué canal genera más volumen." />
            </ChartCardTitle>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData.filter((d) => d.value > 0)} cx="50%" cy="50%" labelLine={false} label={renderPiePercentLabel} outerRadius={90} innerRadius={40} paddingAngle={3} dataKey="value">
                  {pieData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: "0.8rem", paddingTop: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard>
            <ChartCardTitle>
              Tasas de Conversión por Fuente
              <MetricTooltip text="Evolución diaria de la tasa de conversión para cada fuente. Permite comparar qué canal cierra más conversaciones en relación con las que inicia." />
            </ChartCardTitle>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeSeries} margin={{ top: 8, right: 24, left: 0, bottom: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="date" tickFormatter={axisDateTick} minTickGap={24} interval="preserveStartEnd" height={32} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
                <Line type="monotone" dataKey="fbFormConversionRate" stroke={COLORS[0]} strokeWidth={2.5} name="Formularios FB %" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                <Line type="monotone" dataKey="fbAdsConversionRate" stroke={COLORS[1]} strokeWidth={2.5} name="Anuncios FB %" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                <Line type="monotone" dataKey="organicConversionRate" stroke={COLORS[2]} strokeWidth={2.5} name="Orgánico %" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </ChartsGrid>
      )}

      {subTab === "data" && (
        <ChartCard>
          <ChartCardTitle>Datos de Análisis de Fuentes
            <MetricTooltip text="Tabla diaria con el desglose de conversaciones por fuente y su tasa de conversión individual." />
          </ChartCardTitle>
          <TableScroll>
            <WidgetTable>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th className="align-right">Formularios FB</th>
                  <th className="align-right">Anuncios FB</th>
                  <th className="align-right">Orgánico</th>
                  <th className="align-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {timeSeries.map((row) => (
                  <tr key={row.date}>
                    <td>{row.date}</td>
                    <td className="align-right">{row.fbFormClients} ({formatPercentage(row.fbFormConversionRate)})</td>
                    <td className="align-right">{row.fbAdsClients} ({formatPercentage(row.fbAdsConversionRate)})</td>
                    <td className="align-right">{row.organicClients} ({formatPercentage(row.organicConversionRate)})</td>
                    <td className="align-right"><strong>{row.fbFormClients + row.fbAdsClients + row.organicClients}</strong></td>
                  </tr>
                ))}
              </tbody>
            </WidgetTable>
          </TableScroll>
        </ChartCard>
      )}
    </WidgetSection>
  );
};

export default ClientSourceAnalysisWidget;
